/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
  RefreshControl,
} from 'react-native';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
  MaterialIcons,
  Feather,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import * as Calendar from 'expo-calendar';

const { width, height } = Dimensions.get('window');

const UniversityApplications = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [applicationProgress, setApplicationProgress] = useState(35);
  const [savedApplications, setSavedApplications] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [showAllUniversities, setShowAllUniversities] = useState(false);
  const [showAllDeadlines, setShowAllDeadlines] = useState(false);

  // Form states
  const [personalStatement, setPersonalStatement] = useState('');
  const [personalStatementWordCount, setPersonalStatementWordCount] = useState(0);
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    predictedGrades: '',
    previousQualifications: '',
    workExperience: '',
    extracurriculars: '',
    references: '',
  });

  // Mock data
  const ukUniversities = [
    {
      id: 'U001',
      name: 'University of Cambridge',
      location: 'Cambridge, England',
      ranking: '#1 in UK (Engineering)',
      applicationDeadline: '15 October',
      entryRequirements: 'A*AA including Mathematics and Physics',
      intlTuition: '£35,000-£45,000/year',
      specializations: ['Civil', 'Mechanical', 'Electrical', 'Chemical'],
      color: '#002147',
      icon: 'school',
      isSaved: false,
    },
    {
      id: 'U002',
      name: 'University of Oxford',
      location: 'Oxford, England',
      ranking: '#2 in UK (Engineering)',
      applicationDeadline: '15 October',
      entryRequirements: 'A*A*A including Mathematics and Physics',
      intlTuition: '£38,000-£48,000/year',
      specializations: ['Engineering Science', 'Biomedical', 'Materials', 'Aerospace'],
      color: '#002147',
      icon: 'school',
      isSaved: false,
    },
    {
      id: 'U003',
      name: 'Imperial College London',
      location: 'London, England',
      ranking: '#3 in UK (Engineering)',
      applicationDeadline: '15 January',
      entryRequirements: 'A*A*A including Mathematics and Physics',
      intlTuition: '£36,000-£42,000/year',
      specializations: ['Aeronautical', 'Computing', 'Electrical', 'Chemical'],
      color: '#00549F',
      icon: 'school',
      isSaved: false,
    },
    {
      id: 'U004',
      name: 'University of Manchester',
      location: 'Manchester, England',
      ranking: '#4 in UK (Engineering)',
      applicationDeadline: '25 January',
      entryRequirements: 'AAA including Mathematics and Science',
      intlTuition: '£28,000-£35,000/year',
      specializations: ['Chemical', 'Mechanical', 'Civil', 'Computer Science'],
      color: '#E31B23',
      icon: 'school',
      isSaved: false,
    },
    {
      id: 'U005',
      name: 'University of Edinburgh',
      location: 'Edinburgh, Scotland',
      ranking: '#5 in UK (Engineering)',
      applicationDeadline: '25 January',
      entryRequirements: 'AAA-AAB including Mathematics',
      intlTuition: '£26,000-£32,000/year',
      specializations: ['Civil', 'Electronics', 'Chemical', 'Mechanical'],
      color: '#00205B',
      icon: 'school',
      isSaved: false,
    },
    {
      id: 'U006',
      name: 'University of Bristol',
      location: 'Bristol, England',
      ranking: '#6 in UK (Engineering)',
      applicationDeadline: '25 January',
      entryRequirements: 'A*AA including Mathematics',
      intlTuition: '£29,000-£38,000/year',
      specializations: ['Aerospace', 'Civil', 'Mechanical', 'Electrical'],
      color: '#8C2318',
      icon: 'school',
      isSaved: false,
    },
  ];

  const timelineData = [
    {
      id: '1',
      title: 'Research & University Selection',
      description: 'Research universities, courses, and entry requirements',
      deadline: 'June - August',
      status: 'completed',
      icon: 'search',
      color: '#4CAF50',
    },
    {
      id: '2',
      title: 'UCAS Application Preparation',
      description: 'Prepare personal statement, references, and academic transcripts',
      deadline: 'September',
      status: 'in-progress',
      icon: 'file-document-edit',
      color: '#2196F3',
    },
    {
      id: '3',
      title: 'Submit UCAS Application',
      description: 'Submit application through UCAS portal',
      deadline: '15 October (Oxbridge) / 25 January (Most courses)',
      status: 'upcoming',
      icon: 'send',
      color: '#FF9800',
    },
    {
      id: '4',
      title: 'Admissions Tests',
      description: 'Prepare for and take required admissions tests (ENGAA, PAT, etc.)',
      deadline: 'October - November',
      status: 'upcoming',
      icon: 'clipboard-text',
      color: '#9C27B0',
    },
    {
      id: '5',
      title: 'Interviews',
      description: 'Prepare for and attend university interviews',
      deadline: 'November - December',
      status: 'upcoming',
      icon: 'account-voice',
      color: '#E91E63',
    },
    {
      id: '6',
      title: 'Offers & Decisions',
      description: 'Receive offers and make firm/insurance choices',
      deadline: 'January - May',
      status: 'upcoming',
      icon: 'checkbox-marked-circle',
      color: '#00BCD4',
    },
  ];

  const admissionTests = [
    {
      id: 'T1',
      name: 'ENGAA',
      title: 'Engineering Admissions Assessment',
      universities: ['Cambridge'],
      duration: '2 hours',
      format: 'Paper-based',
      sections: ['Mathematics & Physics', 'Advanced Mathematics & Physics'],
      preparationTime: '8-12 weeks',
      difficulty: 'High',
      color: '#002147',
      icon: 'calculator',
    },
    {
      id: 'T2',
      name: 'PAT',
      title: 'Physics Aptitude Test',
      universities: ['Oxford'],
      duration: '2 hours',
      format: 'Paper-based',
      sections: ['Mathematics', 'Physics'],
      preparationTime: '10-12 weeks',
      difficulty: 'High',
      color: '#002147',
      icon: 'atom',
    },
    {
      id: 'T3',
      name: 'TMUA',
      title: 'Test of Mathematics for University Admission',
      universities: ['Cambridge', 'LSE', 'Warwick'],
      duration: '2.5 hours',
      format: 'Computer-based',
      sections: ['Mathematical Reasoning', 'Mathematical Comprehension'],
      preparationTime: '6-8 weeks',
      difficulty: 'Medium-High',
      color: '#00549F',
      icon: 'function',
    },
  ];

  const scholarships = [
    {
      id: 'S1',
      name: 'Chevening Scholarship',
      amount: 'Full tuition + living expenses',
      deadline: 'November 1',
      eligibility: 'International students with leadership potential',
      coverage: 'Tuition, living costs, flights',
      color: '#1E3A8A',
      icon: 'trophy',
    },
    {
      id: 'S2',
      name: 'Commonwealth Scholarship',
      amount: 'Full funding',
      deadline: 'December',
      eligibility: 'Commonwealth citizens',
      coverage: 'Tuition, living allowance, travel',
      color: '#065F46',
      icon: 'earth',
    },
    {
      id: 'S3',
      name: 'University-specific Scholarships',
      amount: '£2,000 - £10,000',
      deadline: 'Varies by university',
      eligibility: 'Academic excellence',
      coverage: 'Partial to full tuition',
      color: '#7C3AED',
      icon: 'school',
    },
  ];

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
  }, []);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDarkModePreference(),
        loadSavedApplications(),
        loadApplicationForm(),
        loadPersonalStatement(),
      ]);
      initializeTimeline();
      initializeDeadlines();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadSavedApplications = async () => {
    try {
      const saved = await AsyncStorage.getItem('savedUniversityApplications');
      if (saved) {
        setSavedApplications(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved applications:', error);
    }
  };

  const loadApplicationForm = async () => {
    try {
      const formData = await AsyncStorage.getItem('universityApplicationForm');
      if (formData) {
        setApplicationForm(JSON.parse(formData));
      }
    } catch (error) {
      console.error('Error loading application form:', error);
    }
  };

  const loadPersonalStatement = async () => {
    try {
      const statement = await AsyncStorage.getItem('personalStatement');
      if (statement) {
        setPersonalStatement(statement);
        setPersonalStatementWordCount(countWords(statement));
      }
    } catch (error) {
      console.error('Error loading personal statement:', error);
    }
  };

  const initializeTimeline = () => {
    setTimelineEvents(timelineData);
  };

  const initializeDeadlines = () => {
    const upcomingDeadlines = [
      { id: 'D1', title: 'UCAS Application (Oxbridge)', date: '15 October', daysLeft: 45, color: '#EF4444' },
      { id: 'D2', title: 'UCAS Application (Most courses)', date: '25 January', daysLeft: 147, color: '#F59E0B' },
      { id: 'D3', title: 'Chevening Scholarship', date: '1 November', daysLeft: 62, color: '#10B981' },
      { id: 'D4', title: 'University-specific Scholarships', date: 'Varies', daysLeft: null, color: '#3B82F6' },
    ];
    setDeadlines(upcomingDeadlines);
  };

  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handlePersonalStatementChange = (text) => {
    setPersonalStatement(text);
    setPersonalStatementWordCount(countWords(text));
    savePersonalStatement(text);
  };

  const savePersonalStatement = async (text) => {
    try {
      await AsyncStorage.setItem('personalStatement', text);
    } catch (error) {
      console.error('Error saving personal statement:', error);
    }
  };

  const saveApplicationForm = async () => {
    try {
      await AsyncStorage.setItem('universityApplicationForm', JSON.stringify(applicationForm));
      Alert.alert('Success', 'Application form saved successfully!');
    } catch (error) {
      console.error('Error saving application form:', error);
      Alert.alert('Error', 'Failed to save application form');
    }
  };

  const toggleUniversitySave = (universityId) => {
    const updatedUniversities = ukUniversities.map(university => {
      if (university.id === universityId) {
        const updated = { ...university, isSaved: !university.isSaved };
        if (updated.isSaved) {
          addToSavedApplications(updated);
        } else {
          removeFromSavedApplications(universityId);
        }
        return updated;
      }
      return university;
    });
    
    // Update UI
    const university = ukUniversities.find(u => u.id === universityId);
    if (university) {
      Alert.alert(
        university.isSaved ? 'Removed from Saved' : 'Added to Saved',
        `${university.name} has been ${university.isSaved ? 'removed from' : 'added to'} your saved applications`
      );
    }
  };

  const addToSavedApplications = async (university) => {
    try {
      const updated = [...savedApplications, university];
      setSavedApplications(updated);
      await AsyncStorage.setItem('savedUniversityApplications', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving application:', error);
    }
  };

  const removeFromSavedApplications = async (universityId) => {
    try {
      const updated = savedApplications.filter(app => app.id !== universityId);
      setSavedApplications(updated);
      await AsyncStorage.setItem('savedUniversityApplications', JSON.stringify(updated));
    } catch (error) {
      console.error('Error removing application:', error);
    }
  };

  const addToCalendar = async (event) => {
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      if (status === 'granted') {
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
        const defaultCalendar = calendars.find(c => c.isPrimary) || calendars[0];
        
        if (defaultCalendar) {
          const eventDate = new Date();
          // Parse date from event (simplified for demo)
          eventDate.setDate(eventDate.getDate() + (event.daysLeft || 30));
          
          await Calendar.createEventAsync(defaultCalendar.id, {
            title: `Deadline: ${event.title}`,
            startDate: eventDate,
            endDate: new Date(eventDate.getTime() + 60 * 60 * 1000), // 1 hour later
            location: 'UCAS/University Portal',
            notes: `Important deadline for university applications`,
          });
          
          Alert.alert('Success', 'Deadline added to your calendar!');
        }
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      Alert.alert('Error', 'Could not add to calendar');
    }
  };

  const shareApplicationProgress = async () => {
    try {
      await Share.share({
        title: 'My UK University Application Progress',
        message: `📚 I've completed ${applicationProgress}% of my UK engineering university applications! 🎓\n\nTrack your progress with the Engineering Learning Hub app!`,
      });
    } catch (error) {
      console.error('Error sharing progress:', error);
    }
  };

  const openUCASWebsite = () => {
    Linking.openURL('https://www.ucas.com/');
  };

  const openUniversityWebsite = (website) => {
    if (website) {
      Linking.openURL(website);
    } else {
      Alert.alert('Info', 'Website URL not available for this university');
    }
  };

  const calculateProgress = () => {
    let progress = 0;
    
    // Personal statement (max 25%)
    if (personalStatementWordCount >= 100) progress += 25;
    else if (personalStatementWordCount > 0) progress += (personalStatementWordCount / 100) * 25;
    
    // Application form (max 30%)
    const formFields = Object.values(applicationForm);
    const completedFormFields = formFields.filter(value => value.trim().length > 0).length;
    progress += (completedFormFields / formFields.length) * 30;
    
    // Selected universities (max 20%)
    if (selectedUniversities.length >= 1) progress += 20;
    
    // Timeline progress (max 25%)
    const completedTimeline = timelineEvents.filter(event => event.status === 'completed').length;
    progress += (completedTimeline / timelineEvents.length) * 25;
    
    return Math.min(Math.round(progress), 100);
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    pink: ['#d53f8c', '#fbb6ce'],
    teal: ['#2d5e43', '#38a169'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
  };

  const styles = getStyles(darkMode);

  const renderHeaderWithTabs = () => (
    <View style={styles.headerWithTabsContainer}>
      {/* Simple Header */}
      <View style={styles.simpleHeader}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={20} color={darkMode ? "#f7fafc" : "#1f2937"} />
        </TouchableOpacity>
        <Text style={styles.simpleHeaderTitle}>🎓 UK University Apps</Text>
        <TouchableOpacity 
          style={styles.shareButton}
          onPress={shareApplicationProgress}
        >
          <MaterialCommunityIcons name="share-variant" size={20} color={darkMode ? "#f7fafc" : "#1f2937"} />
        </TouchableOpacity>
      </View>
      
      {/* Tab Navigation AT THE VERY TOP under header - HORIZONTAL SCROLL */}
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.tabScrollContainer}
        contentContainerStyle={styles.tabContentContainer}
      >
        {[
          { id: 'overview', label: 'Overview', icon: 'view-dashboard' },
          { id: 'universities', label: 'Universities', icon: 'school' },
          { id: 'timeline', label: 'Timeline', icon: 'calendar-clock' },
          { id: 'personal', label: 'Personal', icon: 'file-document-edit' },
          { id: 'tests', label: 'Tests', icon: 'clipboard-text' },
          { id: 'scholarships', label: 'Scholarships', icon: 'trophy' },
          { id: 'resources', label: 'Resources', icon: 'book-open-variant' },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              selectedTab === tab.id && styles.tabButtonActive,
            ]}
            onPress={() => setSelectedTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={16} 
              color={selectedTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              selectedTab === tab.id && styles.tabTextActive,
            ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderCompactProgressOverview = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>📊 Application Progress</Text>
        <TouchableOpacity onPress={() => Alert.alert('Progress Details', `Your overall progress is ${calculateProgress()}%`)}>
          <Text style={styles.seeAllText}>{calculateProgress()}%</Text>
        </TouchableOpacity>
      </View>
      
      <View style={styles.compactProgressCard}>
        <View style={styles.progressBarContainer}>
          <LinearGradient
            colors={gradients.success}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.progressFill, { width: `${calculateProgress()}%` }]}
          />
        </View>
        
        <View style={styles.compactProgressStats}>
          <View style={styles.compactStatItem}>
            <MaterialCommunityIcons name="file-document" size={16} color="#667eea" />
            <Text style={styles.compactStatNumber}>{personalStatementWordCount}</Text>
            <Text style={styles.compactStatLabel}>Words</Text>
          </View>
          <View style={styles.compactStatItem}>
            <MaterialCommunityIcons name="school" size={16} color="#667eea" />
            <Text style={styles.compactStatNumber}>{savedApplications.length}</Text>
            <Text style={styles.compactStatLabel}>Saved</Text>
          </View>
          <View style={styles.compactStatItem}>
            <MaterialCommunityIcons name="calendar-check" size={16} color="#667eea" />
            <Text style={styles.compactStatNumber}>
              {timelineEvents.filter(e => e.status === 'completed').length}/{timelineEvents.length}
            </Text>
            <Text style={styles.compactStatLabel}>Tasks</Text>
          </View>
          <View style={styles.compactStatItem}>
            <MaterialCommunityIcons name="clock-alert" size={16} color="#667eea" />
            <Text style={styles.compactStatNumber}>{deadlines.length}</Text>
            <Text style={styles.compactStatLabel}>Deadlines</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderCompactDeadlines = () => {
    const displayDeadlines = showAllDeadlines ? deadlines : deadlines.slice(0, 2);
    
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>⏰ Upcoming Deadlines</Text>
          <TouchableOpacity onPress={() => setShowAllDeadlines(!showAllDeadlines)}>
            <Text style={styles.seeAllText}>
              {showAllDeadlines ? 'Less' : `All (${deadlines.length})`}
            </Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.compactDeadlinesContainer}>
          {displayDeadlines.map((deadline) => (
            <TouchableOpacity 
              key={deadline.id} 
              style={styles.compactDeadlineItem}
              onPress={() => addToCalendar(deadline)}
            >
              <View style={[styles.deadlineDot, { backgroundColor: deadline.color }]} />
              <View style={styles.compactDeadlineContent}>
                <Text style={styles.compactDeadlineTitle} numberOfLines={1}>
                  {deadline.title}
                </Text>
                <Text style={styles.compactDeadlineDate}>
                  {deadline.date} • {deadline.daysLeft !== null ? `${deadline.daysLeft}d left` : 'Check website'}
                </Text>
              </View>
              <MaterialCommunityIcons name="calendar-plus" size={16} color="#667eea" />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  const renderCompactUniversities = () => {
    const displayUniversities = showAllUniversities ? ukUniversities : ukUniversities.slice(0, 2);
    
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>🏛️ Top Universities</Text>
          <TouchableOpacity onPress={() => setShowAllUniversities(!showAllUniversities)}>
            <Text style={styles.seeAllText}>
              {showAllUniversities ? 'Less' : `All (${ukUniversities.length})`}
            </Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          style={styles.compactUniversitiesScroll}
        >
          {displayUniversities.map((university) => (
            <View key={university.id} style={styles.compactUniversityCard}>
              <LinearGradient
                colors={[university.color, `${university.color}80`]}
                style={styles.compactUniversityHeader}
              >
                <MaterialCommunityIcons name={university.icon} size={20} color="#ffffff" />
                <Text style={styles.compactUniversityName} numberOfLines={2}>
                  {university.name}
                </Text>
              </LinearGradient>
              
              <View style={styles.compactUniversityContent}>
                <View style={styles.compactUniversityDetails}>
                  <View style={styles.compactDetailRow}>
                    <MaterialCommunityIcons name="calendar-alert" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                    <Text style={styles.compactDetailText} numberOfLines={1}>
                      {university.applicationDeadline}
                    </Text>
                  </View>
                  <View style={styles.compactDetailRow}>
                    <MaterialCommunityIcons name="cash" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                    <Text style={styles.compactDetailText} numberOfLines={1}>
                      {university.intlTuition}
                    </Text>
                  </View>
                </View>
                
                <View style={styles.compactUniversityActions}>
                  <TouchableOpacity 
                    style={styles.compactSaveButton}
                    onPress={() => toggleUniversitySave(university.id)}
                  >
                    <MaterialCommunityIcons 
                      name={university.isSaved ? "bookmark" : "bookmark-outline"} 
                      size={16} 
                      color={university.isSaved ? "#EF4444" : (darkMode ? "#a0aec0" : "#6b7280")} 
                    />
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={styles.compactUcasButton}
                    onPress={() => openUniversityWebsite(`https://www.${String(university.name ?? '').toLowerCase().replace(/\s+/g, '')}.ac.uk`)}
                  >
                    <Text style={styles.compactUcasButtonText}>Apply</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderUniversityCards = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>🏛️ Top Engineering Universities</Text>
        <TouchableOpacity onPress={() => Alert.alert('Info', 'More universities coming soon!')}>
          <Text style={styles.seeAllText}>View All</Text>
        </TouchableOpacity>
      </View>
      
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {ukUniversities.map((university) => (
          <View key={university.id} style={styles.universityCard}>
            <LinearGradient
              colors={[university.color, `${university.color}80`]}
              style={styles.universityHeader}
            >
              <View style={styles.universityLogoContainer}>
                <MaterialCommunityIcons name={university.icon} size={28} color="#ffffff" />
              </View>
              <View style={styles.universityTitleContainer}>
                <Text style={styles.universityName} numberOfLines={2}>{university.name}</Text>
                <Text style={styles.universityLocation}>{university.location}</Text>
                <Text style={styles.universityRanking}>{university.ranking}</Text>
              </View>
            </LinearGradient>
            
            <View style={styles.universityContent}>
              <View style={styles.universityDetails}>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="calendar-alert" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.detailText}>Deadline: {university.applicationDeadline}</Text>
                </View>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="school" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.detailText}>Entry: {university.entryRequirements}</Text>
                </View>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="cash" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.detailText}>Intl Fees: {university.intlTuition}</Text>
                </View>
              </View>
              
              <View style={styles.universityActions}>
                <TouchableOpacity 
                  style={styles.saveButton}
                  onPress={() => toggleUniversitySave(university.id)}
                >
                  <MaterialCommunityIcons 
                    name={university.isSaved ? "bookmark" : "bookmark-outline"} 
                    size={16} 
                    color={university.isSaved ? "#EF4444" : (darkMode ? "#a0aec0" : "#6b7280")} 
                  />
                  <Text style={styles.saveButtonText}>
                    {university.isSaved ? 'Saved' : 'Save'}
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.ucasButton}
                  onPress={() => openUniversityWebsite(`https://www.${String(university.name ?? '').toLowerCase().replace(/\s+/g, '')}.ac.uk`)}
                >
                  <MaterialCommunityIcons name="open-in-new" size={16} color="#ffffff" />
                  <Text style={styles.ucasButtonText}>Apply via UCAS</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const renderTimeline = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>📅 Application Timeline</Text>
      
      <View style={styles.timelineContainer}>
        {timelineEvents.map((event, index) => (
          <View key={event.id} style={styles.timelineItem}>
            <View style={styles.timelineLeft}>
              <View style={[
                styles.timelineIcon,
                { backgroundColor: event.color }
              ]}>
                <MaterialCommunityIcons name={event.icon} size={16} color="#ffffff" />
              </View>
              {index < timelineEvents.length - 1 && (
                <View style={styles.timelineLine} />
              )}
            </View>
            
            <View style={styles.timelineContent}>
              <View style={styles.timelineHeader}>
                <Text style={styles.timelineTitle}>{event.title}</Text>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: 
                    event.status === 'completed' ? '#10B981' : 
                    event.status === 'in-progress' ? '#F59E0B' : '#6B7280'
                  }
                ]}>
                  <Text style={styles.statusText}>
                    {event.status === 'completed' ? '✓ Completed' : 
                     event.status === 'in-progress' ? '⏳ In Progress' : '📅 Upcoming'}
                  </Text>
                </View>
              </View>
              
              <Text style={styles.timelineDescription}>{event.description}</Text>
              <View style={styles.timelineDeadline}>
                <MaterialCommunityIcons name="calendar-clock" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.timelineDate}>{event.deadline}</Text>
              </View>
              
              {event.status === 'upcoming' && (
                <TouchableOpacity style={styles.reminderButton}>
                  <MaterialCommunityIcons name="bell-plus" size={12} color="#667eea" />
                  <Text style={styles.reminderButtonText}>Set Reminder</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const renderPersonalStatement = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>✍️ Personal Statement</Text>
      
      <View style={styles.personalStatementCard}>
        <View style={styles.wordCountContainer}>
          <Text style={styles.wordCountText}>Word Count: {personalStatementWordCount}/4000</Text>
          <View style={styles.wordCountProgress}>
            <LinearGradient
              colors={gradients.success}
              style={[styles.wordCountFill, { width: `${Math.min((personalStatementWordCount / 4000) * 100, 100)}%` }]}
            />
          </View>
        </View>
        
        <TextInput
          style={styles.personalStatementInput}
          placeholder="Start writing your personal statement here...\n\n• Why do you want to study engineering?\n• What relevant experience do you have?\n• What are your career aspirations?\n• Why are you suitable for the course?"
          value={personalStatement}
          onChangeText={handlePersonalStatementChange}
          multiline
          numberOfLines={8}
          textAlignVertical="top"
          placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
        />
        
        <View style={styles.tipsContainer}>
          <Text style={styles.tipsTitle}>💡 Tips for a great personal statement:</Text>
          <View style={styles.tipItem}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#10B981" />
            <Text style={styles.tipText}>Be specific about why you want to study engineering</Text>
          </View>
          <View style={styles.tipItem}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#10B981" />
            <Text style={styles.tipText}>Include relevant work experience or projects</Text>
          </View>
          <View style={styles.tipItem}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#10B981" />
            <Text style={styles.tipText}>Show enthusiasm and passion for the subject</Text>
          </View>
          <View style={styles.tipItem}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#10B981" />
            <Text style={styles.tipText}>Proofread carefully for grammar and spelling</Text>
          </View>
        </View>
        
        <View style={styles.statementActions}>
          <TouchableOpacity style={styles.saveStatementButton} onPress={() => savePersonalStatement(personalStatement)}>
            <MaterialCommunityIcons name="content-save" size={14} color="#ffffff" />
            <Text style={styles.saveStatementText}>Save Draft</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.reviewButton} onPress={() => Alert.alert('Coming Soon', 'AI review feature coming soon!')}>
            <MaterialCommunityIcons name="robot" size={14} color="#ffffff" />
            <Text style={styles.reviewButtonText}>AI Review</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderAdmissionTests = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>📝 Admission Tests</Text>
      
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {admissionTests.map((test) => (
          <View key={test.id} style={styles.testCard}>
            <LinearGradient
              colors={[test.color, `${test.color}80`]}
              style={styles.testHeader}
            >
              <View style={styles.testIconContainer}>
                <MaterialCommunityIcons name={test.icon} size={24} color="#ffffff" />
              </View>
              <View style={styles.testTitleContainer}>
                <Text style={styles.testName}>{test.name}</Text>
                <Text style={styles.testFullName}>{test.title}</Text>
              </View>
            </LinearGradient>
            
            <View style={styles.testContent}>
              <View style={styles.testDetails}>
                <View style={styles.testDetail}>
                  <MaterialCommunityIcons name="clock-outline" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.testDetailText}>{test.duration}</Text>
                </View>
                <View style={styles.testDetail}>
                  <MaterialCommunityIcons name="format-list-bulleted" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.testDetailText}>{test.format}</Text>
                </View>
                <View style={styles.testDetail}>
                  <MaterialCommunityIcons name="calendar-range" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.testDetailText}>{test.preparationTime} prep</Text>
                </View>
                <View style={styles.testDetail}>
                  <MaterialCommunityIcons name="trending-up" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.testDetailText}>{test.difficulty}</Text>
                </View>
              </View>
              
              <Text style={styles.testUniversities}>
                <MaterialCommunityIcons name="school" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                Required by: {test.universities.join(', ')}
              </Text>
              
              <TouchableOpacity style={styles.practiceButton}>
                <MaterialCommunityIcons name="book-open-variant" size={14} color="#ffffff" />
                <Text style={styles.practiceButtonText}>Practice Tests</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const renderScholarships = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>💰 Scholarships & Funding</Text>
      
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {scholarships.map((scholarship) => (
          <View key={scholarship.id} style={styles.scholarshipCard}>
            <LinearGradient
              colors={[scholarship.color, `${scholarship.color}80`]}
              style={styles.scholarshipHeader}
            >
              <View style={styles.scholarshipIconContainer}>
                <MaterialCommunityIcons name={scholarship.icon} size={24} color="#ffffff" />
              </View>
              <View style={styles.scholarshipTitleContainer}>
                <Text style={styles.scholarshipName}>{scholarship.name}</Text>
                <Text style={styles.scholarshipAmount}>{scholarship.amount}</Text>
              </View>
            </LinearGradient>
            
            <View style={styles.scholarshipContent}>
              <View style={styles.scholarshipDetails}>
                <View style={styles.scholarshipDetail}>
                  <MaterialCommunityIcons name="calendar-alert" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.scholarshipDetailText}>Deadline: {scholarship.deadline}</Text>
                </View>
                <View style={styles.scholarshipDetail}>
                  <MaterialCommunityIcons name="account-check" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.scholarshipDetailText}>{scholarship.eligibility}</Text>
                </View>
                <View style={styles.scholarshipDetail}>
                  <MaterialCommunityIcons name="cash-multiple" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.scholarshipDetailText}>Covers: {scholarship.coverage}</Text>
                </View>
              </View>
              
              <TouchableOpacity style={styles.applyButton}>
                <MaterialCommunityIcons name="send" size={14} color="#ffffff" />
                <Text style={styles.applyButtonText}>Apply Now</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const renderResources = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>📚 Application Resources</Text>
      
      <View style={styles.resourcesGrid}>
        <TouchableOpacity style={styles.resourceCard} onPress={openUCASWebsite}>
          <LinearGradient
            colors={gradients.primary}
            style={styles.resourceIcon}
          >
            <MaterialCommunityIcons name="web" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.resourceTitle}>UCAS Portal</Text>
          <Text style={styles.resourceDescription}>Official application portal for UK universities</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.resourceCard} onPress={() => Linking.openURL('https://www.ucas.com/undergraduate/applying-university/writing-personal-statement')}>
          <LinearGradient
            colors={gradients.success}
            style={styles.resourceIcon}
          >
            <MaterialCommunityIcons name="file-document-edit" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.resourceTitle}>Personal Statement Guide</Text>
          <Text style={styles.resourceDescription}>Official UCAS guide for writing personal statements</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.resourceCard} onPress={() => Linking.openURL('https://www.admissionstesting.org/')}>
          <LinearGradient
            colors={gradients.warning}
            style={styles.resourceIcon}
          >
            <MaterialCommunityIcons name="clipboard-text" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.resourceTitle}>Admission Tests</Text>
          <Text style={styles.resourceDescription}>Practice materials for engineering admission tests</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.resourceCard} onPress={() => Linking.openURL('https://www.ukcisa.org.uk/')}>
          <LinearGradient
            colors={gradients.info}
            style={styles.resourceIcon}
          >
            <MaterialCommunityIcons name="passport" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.resourceTitle}>Visa & Immigration</Text>
          <Text style={styles.resourceDescription}>Information for international students</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderSelectedTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return (
          <>
            {renderCompactProgressOverview()}
            {renderCompactDeadlines()}
            {renderCompactUniversities()}
          </>
        );
      case 'universities':
        return renderUniversityCards();
      case 'timeline':
        return renderTimeline();
      case 'personal':
        return renderPersonalStatement();
      case 'tests':
        return renderAdmissionTests();
      case 'scholarships':
        return renderScholarships();
      case 'resources':
        return renderResources();
      default:
        return renderCompactProgressOverview();
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color={darkMode ? "#667eea" : "#667eea"} />
        <Text style={styles.loadingText}>Loading application resources...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "dark-content"} backgroundColor={darkMode ? "#1a202c" : "#ffffff"} />
      
      {renderHeaderWithTabs()}
      
      <View style={styles.mainContainer}>
        {/* Scroll Area with NO GAP - starts immediately after tabs */}
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={refreshData}
              colors={['#667eea']}
              tintColor="#667eea"
            />
          }
        >
          {renderSelectedTabContent()}
          
          <View style={styles.ctaSection}>
            <LinearGradient
              colors={gradients.success}
              style={styles.ctaCard}
            >
              <MaterialCommunityIcons name="school" size={40} color="#ffffff" />
              <Text style={styles.ctaTitle}>Ready to Apply?</Text>
              <Text style={styles.ctaDescription}>
                Start your UK engineering university application today. We're here to help you every step of the way!
              </Text>
              <TouchableOpacity style={styles.ctaButton} onPress={openUCASWebsite}>
                <Text style={styles.ctaButtonText}>Go to UCAS Portal</Text>
                <MaterialCommunityIcons name="arrow-right" size={18} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>
          </View>
          
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  mainContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  
  // New header with tabs container
  headerWithTabsContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  
  // Simple header (no gradient)
  simpleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
    height: 54,
  },
  simpleHeaderTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  backButton: {
    padding: 5,
  },
  shareButton: {
    padding: 5,
  },
  
  // Tab Navigation - HORIZONTAL SCROLL at the top
  tabScrollContainer: {
    height: 44,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
  },
  tabContentContainer: {
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginHorizontal: 4,
    minWidth: 90,
    justifyContent: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#667eea',
  },
  tabText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '500',
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  tabTextActive: {
    color: '#ffffff',
  },
  
  // Scroll Area - NO GAP with Tabs
  scrollView: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  scrollContent: {
    paddingBottom: 20,
    paddingTop: 0, // NO gap - content starts immediately
  },
  
  // Section styles - adjusted for no gap
  section: {
    marginHorizontal: 14,
    marginBottom: 16,
    marginTop: 12, // Small top margin for first section
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  seeAllText: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
  },
  
  // Compact Progress Overview - Match the design
  compactProgressCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    marginTop: 0,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 16,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  compactProgressStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compactStatItem: {
    alignItems: 'center',
    flex: 1,
  },
  compactStatNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  compactStatLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  
  // Compact Deadlines - Match the design
  compactDeadlinesContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  compactDeadlineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  deadlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 10,
  },
  compactDeadlineContent: {
    flex: 1,
  },
  compactDeadlineTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 2,
  },
  compactDeadlineDate: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  
  // Compact Universities - Match the design
  compactUniversitiesScroll: {
    marginHorizontal: -14,
    paddingHorizontal: 14,
  },
  compactUniversityCard: {
    width: 180,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    marginRight: 10,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  compactUniversityHeader: {
    padding: 12,
    alignItems: 'center',
    minHeight: 80,
    justifyContent: 'center',
  },
  compactUniversityName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 6,
    textAlign: 'center',
    lineHeight: 16,
  },
  compactUniversityContent: {
    padding: 8,
  },
  compactUniversityDetails: {
    marginBottom: 8,
  },
  compactDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  compactDetailText: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 3,
    flex: 1,
  },
  compactUniversityActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  compactSaveButton: {
    padding: 4,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 4,
  },
  compactUcasButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  compactUcasButtonText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
  },
  
  // Other Tabs - Adjusted for no gap
  progressCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    marginTop: 0,
  },
  progressHeader: {
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  progressPercentage: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  progressStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: 16,
    paddingTop: 0,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginVertical: 6,
  },
  statLabel: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  deadlineCard: {
    width: 260,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    marginRight: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  deadlineHeader: {
    padding: 14,
  },
  deadlineTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
  },
  deadlineDate: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  deadlineContent: {
    padding: 14,
  },
  deadlineDays: {
    fontSize: 13,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 10,
  },
  deadlineNote: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontStyle: 'italic',
  },
  calendarButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  calendarButtonText: {
    fontSize: 11,
    color: '#667eea',
    marginLeft: 3,
  },
  universityCard: {
    width: 300,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    marginRight: 12,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  universityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  universityLogoContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  universityTitleContainer: {
    flex: 1,
  },
  universityName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
  },
  universityLocation: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 3,
  },
  universityRanking: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  universityContent: {
    padding: 14,
  },
  universityDetails: {
    marginBottom: 14,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  detailText: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 5,
    flex: 1,
  },
  universityActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  saveButtonText: {
    fontSize: 11,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 3,
  },
  ucasButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  ucasButtonText: {
    fontSize: 11,
    color: '#ffffff',
    marginLeft: 3,
    fontWeight: '600',
  },
  timelineContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 18,
  },
  timelineLeft: {
    alignItems: 'center',
    marginRight: 12,
  },
  timelineIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  timelineLine: {
    width: 2,
    flex: 1,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    marginTop: 6,
  },
  timelineContent: {
    flex: 1,
    paddingTop: 2,
  },
  timelineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  timelineTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
    marginLeft: 6,
  },
  statusText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: '600',
  },
  timelineDescription: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 6,
    lineHeight: 18,
  },
  timelineDeadline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  timelineDate: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 3,
  },
  reminderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  reminderButtonText: {
    fontSize: 10,
    color: '#667eea',
    marginLeft: 3,
  },
  personalStatementCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  wordCountContainer: {
    marginBottom: 12,
  },
  wordCountText: {
    fontSize: 13,
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 6,
    fontWeight: '600',
  },
  wordCountProgress: {
    height: 5,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 2.5,
    overflow: 'hidden',
  },
  wordCountFill: {
    height: '100%',
    borderRadius: 2.5,
  },
  personalStatementInput: {
    minHeight: 160,
    backgroundColor: darkMode ? '#1a202c' : '#f9fafb',
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    color: darkMode ? '#f7fafc' : '#1f2937',
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#d1d5db',
    marginBottom: 16,
    textAlignVertical: 'top',
  },
  tipsContainer: {
    backgroundColor: darkMode ? '#1a202c' : '#f0f9ff',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
  },
  tipsTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  tipText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 6,
    flex: 1,
    lineHeight: 18,
  },
  statementActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  saveStatementButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    flex: 1,
    marginRight: 6,
    justifyContent: 'center',
  },
  saveStatementText: {
    fontSize: 12,
    color: '#ffffff',
    marginLeft: 6,
    fontWeight: '600',
  },
  reviewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8b5cf6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    flex: 1,
    marginLeft: 6,
    justifyContent: 'center',
  },
  reviewButtonText: {
    fontSize: 12,
    color: '#ffffff',
    marginLeft: 6,
    fontWeight: '600',
  },
  testCard: {
    width: 260,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    marginRight: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  testHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
  },
  testIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  testTitleContainer: {
    flex: 1,
  },
  testName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
  },
  testFullName: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  testContent: {
    padding: 14,
  },
  testDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
    gap: 6,
  },
  testDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    marginRight: 6,
    marginBottom: 6,
  },
  testDetailText: {
    fontSize: 9,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 3,
  },
  testUniversities: {
    fontSize: 11,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 12,
    lineHeight: 16,
  },
  practiceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    justifyContent: 'center',
  },
  practiceButtonText: {
    fontSize: 12,
    color: '#ffffff',
    marginLeft: 6,
    fontWeight: '600',
  },
  scholarshipCard: {
    width: 260,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 14,
    marginRight: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  scholarshipHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
  },
  scholarshipIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  scholarshipTitleContainer: {
    flex: 1,
  },
  scholarshipName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
  },
  scholarshipAmount: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  scholarshipContent: {
    padding: 14,
  },
  scholarshipDetails: {
    marginBottom: 12,
  },
  scholarshipDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  scholarshipDetailText: {
    fontSize: 11,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 6,
    flex: 1,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10B981',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    justifyContent: 'center',
  },
  applyButtonText: {
    fontSize: 12,
    color: '#ffffff',
    marginLeft: 6,
    fontWeight: '600',
  },
  resourcesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  resourceCard: {
    width: '48%',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  resourceIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  resourceTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 6,
    textAlign: 'center',
  },
  resourceDescription: {
    fontSize: 11,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    textAlign: 'center',
    lineHeight: 14,
  },
  ctaSection: {
    marginHorizontal: 14,
    marginBottom: 16,
  },
  ctaCard: {
    borderRadius: 14,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
  },
  ctaTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginVertical: 12,
    textAlign: 'center',
  },
  ctaDescription: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  ctaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
    marginRight: 6,
  },
  bottomPadding: {
    height: 20,
  },
});

export default UniversityApplications;